import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { type ButtonProps, Skeleton, Stack } from "@mui/material";
import { type GetLifeDetailResponse, ProductType } from "@repo/api";
import { readEnv } from "@repo/helpers";
import { useAllowedChanges, type SupportedContractType } from "@repo/logic";
import { HeadingXS, HomepageSmartActionsPanel, useTypedTranslation } from "@repo/ui";
import { isNil } from "lodash";
import { type ElementType, type FC, type PropsWithChildren, useMemo } from "react";
import { contractLink } from "../../../functions";
import {
    useContractById,
    useLifeDetail,
    usePensionDetail,
    usePhoneNumbers,
} from "../../../queryHooks";
import Button from "../../Link/InternalButtonLink";
import { type ActionType, useSmartAction } from "./context";
import { SmartActionWarnings } from "./SmartAction.helpers";
import { SmartActionComponents as SAC } from "./SmartActionComponents";

export type IAttention = {
    text: string;
};

export type SmartAction = {
    type: SupportedContractType;
};

export type SmartActionsByIdProps = {
    id: string;
    type: SupportedContractType;
};
export type SmartActionsByTypeProps = {
    type: SupportedContractType;
    id: string;
};

// TODO fix typing, split into files,
// split into different components by contract type, fix broken loading state display
const SmartActionsByType: FC<SmartActionsByTypeProps> = ({ type, id, ...rest }) => {
    const { data, isSuccess: contractByIdSuccess } = useContractById({
        id,
        options: { refetchOnMount: false, enabled: Boolean(id) },
    });

    const { isSuccess: isDetailSuccess, isError: isDetailError } = usePensionDetail({
        id,
        type,
        options: {
            enabled: type === ProductType.Uf || type === ProductType.Pf,
        },
    });

    const { data: lifeDetail } = useLifeDetail<GetLifeDetailResponse>({
        id,
        contractType: ProductType.Cl,
        options: {
            enabled: type === ProductType.Cl,
        },
    });

    const isFufiContract = type === ProductType.Clf;

    const {
        data: allowedChanges,
        isSuccess: allowedChangesSuccess,
        isError: allowedChangesError,
    } = useAllowedChanges({
        id,
        options: {
            enabled: Boolean(id),
        },
    });

    const phoneNumbers = usePhoneNumbers({
        options: {
            enabled: type === ProductType.Uf,
        },
    });

    if (allowedChangesError || phoneNumbers.isError || isDetailError) return null;

    const authPhoneNumbers = phoneNumbers.isSuccess && phoneNumbers.data;

    const showInvestmentStrategy =
        readEnv("GATSBY_FEATURE_IS_SAVINGS_STRATEGY_ENABLED") === "true" &&
        authPhoneNumbers &&
        allowedChangesSuccess &&
        allowedChanges.canChangeInvestmentStrategy === true;

    const showNewQuestionnaireButton =
        readEnv("GATSBY_IS_NEW_QUESTIONNAIRE_BUTTON_ENABLED") === "true" && showInvestmentStrategy;

    const getIsReady = () => {
        if (isNil(allowedChanges) || !allowedChangesSuccess) return false;

        if (!type || !contractByIdSuccess) return false;

        if (type === ProductType.Uf) {
            return phoneNumbers.isSuccess && allowedChangesSuccess && isDetailSuccess;
        }

        if (type === ProductType.Pf) return isDetailSuccess;

        return true;
    };

    const isContributionChangeDisabled = allowedChanges?.canChangeEmployerOrClientContribution !== true;
    const isInvestmentStrategyChangeDisabled = allowedChanges?.canChangeInvestmentStrategy !== true; //|| data?.isChildrenContract !== false;
   
    const isContactAddressChangeDisabled = allowedChanges?.canChangeContactAddress !== true;
    const allowInflationChange =
        lifeDetail?.contract.inflationProgram !== undefined &&
        lifeDetail.contract.inflationProgram.canUserEditSettings && // basend on aniversary date
        !lifeDetail.contract.inflationProgram.isProcessingInflation; // only allow change if not processing inflation

    const isElectrocnicContactChangeDisabled = allowedChanges?.canChangeElectronicContacts !== true;

    const smartActions: { [key in keyof typeof SAC]: SmartActionListType } = {
        Fond: {
            component: SAC.Fond,
            tab: "smlouva",
            hasCustomEvent: true,
            disabled: isContributionChangeDisabled,
        },
        Inflation: {
            component: SAC.Inflation,
            tab: "smlouva",
            hasCustomEvent: true,
            disabled: !allowInflationChange,
        },
        EmployerContribution: {
            component: SAC.EmployerContribution,
            tab: "smlouva",
            hasCustomEvent: true,
            disabled: isContributionChangeDisabled,
        },
        Changes: {
            component: SAC.Changes,
            url: "/profil/zmeny/",
            hasCustomEvent: false,
        },
        Payments: {
            component: SAC.Payments,
            tab: "pohyby-platby",
            hasCustomEvent: false,
        },
        InvestmentStrategy: {
            component: SAC.InvestmentStrategy,
            tab: "detail",
            hasCustomEvent: showInvestmentStrategy,
            disabled: isInvestmentStrategyChangeDisabled,
        },
        Coverage: {
            component: SAC.Coverage,
            tab: "osoby-kryti",
            hasCustomEvent: false,
        },
        CoverageFufi: {
            component: SAC.CoverageFufi,
            tab: "pojistna-ochrana",
            hasCustomEvent: false,
        },
        TaxCertificate: {
            component: SAC.TaxCertificate,
            tab: "dokumenty",
            hasCustomEvent: false,
        },
        ChangeAdress: {
            component: SAC.ChangeAdress,
            tab: "klient",
            hasCustomEvent: false,
            disabled: isContactAddressChangeDisabled,
        },
        NextPayment: {
            component: SAC.NextPayment,
            tab: "pohyby-platby",
            hasCustomEvent: false,
        },
        Report: {
            component: SAC.Report,
            tab: "dokumenty",
            hasCustomEvent: false,
        },
        NewQuestionnaireButton: {
            component: SAC.NewQuestionnaireButton,
        },
        SmartActionSkeleton: {
            component: SAC.SmartActionSkeleton,
        },
    };

    // renders only the first 3 actions in each product
    const mapSmartActionType: Record<SupportedContractType, SmartActionListType[]> = {
        PF: [
            smartActions.Fond,
            smartActions.EmployerContribution,
            smartActions.Changes,
            smartActions.Payments,
            smartActions.NextPayment,
            smartActions.TaxCertificate,
        ],
        UF: [
            smartActions.Fond,
            data?.isChildrenContract ? smartActions.Payments : smartActions.EmployerContribution,
            showInvestmentStrategy ? smartActions.InvestmentStrategy : smartActions.Changes,
            smartActions.ChangeAdress,
            smartActions.Payments,
            smartActions.NextPayment,
            smartActions.TaxCertificate,
            smartActions.NewQuestionnaireButton,
        ],
        CL: [
            smartActions.Inflation,
            smartActions.Coverage,
            { ...smartActions.Payments, tab: "pohyby" },
            smartActions.Changes,
            smartActions.ChangeAdress,
            { ...smartActions.NextPayment, tab: "pohyby" },
            smartActions.Report,
        ],
        CLF: [
            { ...(isFufiContract ? smartActions.CoverageFufi : smartActions.Coverage) },
            { ...smartActions.Payments, tab: "pohyby" },
            smartActions.Changes,
            smartActions.ChangeAdress,
            { ...smartActions.NextPayment, tab: "pohyby" },
            smartActions.Report,
        ],
    };

    const smartActionComponent = mapSmartActionType[type];

    if (!getIsReady()) {
        return (
            <HomepageSmartActionsPanel isReady={false} isUfType={type === ProductType.Uf} />
        );
    }

    const actionsContent = smartActionComponent
        .filter((v) => !v.disabled)
        .slice(0, 3)
        .map((Item, i) => {
            if (Item.hidden) return null;
            return (
                <Item.component
                    key={`smart-action-${i}`}
                    href={Item.tab ? contractLink(id, data?.type, Item.tab) : Item.url}
                    hasCustomEvent={Item.hasCustomEvent}
                    contractId={id}
                    disabled={Item.disabled}
                    {...rest}
                />
            );
        });

    return (
        <HomepageSmartActionsPanel
            isReady={true}
            isUfType={type === ProductType.Uf}
            actionsContent={actionsContent}
            questionnaireContent={
                showNewQuestionnaireButton ? (
                    <SAC.NewQuestionnaireButton
                        disabled={isInvestmentStrategyChangeDisabled || !showNewQuestionnaireButton}
                    />
                ) : undefined
            }
            warningsContent={<SmartActionWarnings allowedChanges={allowedChanges} />}
        />
    );
};

const SmartActionsById: FC<SmartActionsByIdProps | Record<string, never>> = ({ type, id, ...rest }) => {
    return <SmartActionsByType type={type} id={id} {...rest} />;
};

export type FondProps = {
    amount: number;
};

export type NextPaymentProps = {
    date: string;
} & FondProps;

export type ISmartAction = PropsWithChildren<{
    icon: ButtonProps["startIcon"];
    action: ActionType;
    href: string;
    contractId: string;
    hasCustomEvent: boolean;
    disabled?: boolean;
}>;

type SmartActionListType = {
    component: ElementType;
    url?: string;
    tab?: string;
    hasCustomEvent?: boolean;
    disabled?: boolean;
    hidden?: boolean;
};

const useEventNameByActionName = () => {
    const { t } = useTypedTranslation();
    return useMemo(
        () => ({
            Changes: t("SmartActionListType.Changes"),
            NextPayment: t("SmartActionListType.NextPayment"),
            Payments: t("SmartActionListType.Payments"),
            Report: t("SmartActionListType.Report"),
            Fond: t("SmartActionListType.Fond"),
            TaxCertificate: t("SmartActionListType.TaxCertificate"),
            EmployerContribution: t("SmartActionListType.EmployerContribution"),
            Coverage: t("SmartActionListType.Coverage"),
            CoverageFufi: t("SmartActionListType.CoverageFufi"),
            Inflation: t("SmartActionListType.Inflation"),
            ChangeAdress: t("SmartActionListType.ChangeAdress"),
            InvestmentStrategy: t("SmartActionListType.InvestmentStrategy"),
            FondBankId: t("SmartActionListType.FondBankId"),
            InvestmentStrategyBankId: t("SmartActionListType.InvestmentStrategyBankId"),
            NewQuestionnaireButton: t("SmartActionsButtons.PensionQuestionnaireTitle"),
        }),
        [t],
    );
};

export const SmartAction: FC<ISmartAction> = ({
    children,
    icon,
    action,
    href,
    hasCustomEvent,
    contractId,
    disabled = false,
}) => {
    const { setAction } = useSmartAction();
    const ai = useAppInsightsContext();
    const eventNameByActionName = useEventNameByActionName();

    return (
        <Button
            variant="smartAction"
            disabled={disabled}
            startIcon={icon}
            onClick={() => {
                ai.trackEvent(
                    {
                        name: `${
                            !action || !eventNameByActionName[action] ? "nedefinovaná" : eventNameByActionName[action]
                        } [smartAction]`,
                    },
                    { contractId },
                );
                if (hasCustomEvent) {
                    setAction({ type: action });
                }
            }}
            href={href}
        >
            <Stack sx={{ flexGrow: 1, justifyContent: "center" }}>
                <HeadingXS withoutScale sx={{ mb: 0, fontSize: { md: "1.4rem", xs: "1.2rem" } }}>
                    {children}
                </HeadingXS>
            </Stack>
        </Button>
    );
};

export default SmartActionsById;
